/**
 * Types pour la gestion des permissions et rôles.
 */

export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  category_display: string;
  access_level: 'NONE' | 'READ' | 'WRITE' | 'ADMIN' | 'AUDIT';
  access_level_display: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  is_active: boolean;
  is_system_role: boolean;
  permissions_count: number;
  users_count: number;
  created_at: string;
  updated_at: string;
}

export interface RolePermission {
  id: number;
  permission: Permission;
  permission_id: number;
  access_level: 'NONE' | 'READ' | 'WRITE' | 'ADMIN' | 'AUDIT';
  access_level_display: string;
  granted_at: string;
  granted_by: number;
  granted_by_username: string;
}

export interface UserRole {
  id: number;
  user: number;
  user_username: string;
  user_email: string;
  role: Role;
  role_id: number;
  assigned_at: string;
  assigned_by: number;
  assigned_by_username: string;
  is_active: boolean;
  expires_at?: string;
  is_expired: boolean;
}

export interface PermissionLog {
  id: number;
  user: number;
  user_username: string;
  action: 'GRANT' | 'REVOKE' | 'ASSIGN_ROLE' | 'REMOVE_ROLE' | 'ACCESS_DENIED' | 'ACCESS_GRANTED';
  action_display: string;
  permission?: Permission;
  permission_name?: string;
  role?: Role;
  role_name?: string;
  target_user?: number;
  target_user_username?: string;
  details: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface UserPermission {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  permissions: Permission[];
  roles: Role[];
  permission_codes: string[];
}

// Constantes des permissions
export const PERMISSIONS = {
  // Accès général
  ACCESS_APPLICATION: 'access.application',
  
  // Gestion des utilisateurs
  USER_MANAGEMENT_VIEW: 'user_management.view',
  USER_MANAGEMENT_CREATE: 'user_management.create',
  USER_MANAGEMENT_EDIT: 'user_management.edit',
  USER_MANAGEMENT_DELETE: 'user_management.delete',
  USER_MANAGEMENT_ASSIGN_ROLES: 'user_management.assign_roles',
  
  // Configuration système
  SYSTEM_CONFIG_VIEW: 'system_config.view',
  SYSTEM_CONFIG_EDIT: 'system_config.edit',
  SYSTEM_CONFIG_MANAGE_SITES: 'system_config.manage_sites',
  SYSTEM_CONFIG_MANAGE_TENANTS: 'system_config.manage_tenants',
  
  // Paramètres
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',
  
  // Pesée
  WEIGHING_VIEW: 'weighing.view',
  WEIGHING_CREATE: 'weighing.create',
  WEIGHING_EDIT: 'weighing.edit',
  WEIGHING_DELETE: 'weighing.delete',
  WEIGHING_CANCEL: 'weighing.cancel',
  WEIGHING_COMPLETE: 'weighing.complete',
  
  // Analyse qualité
  QUALITY_ANALYSIS_VIEW: 'quality_analysis.view',
  QUALITY_ANALYSIS_CREATE: 'quality_analysis.create',
  QUALITY_ANALYSIS_EDIT: 'quality_analysis.edit',
  QUALITY_ANALYSIS_APPROVE: 'quality_analysis.approve',
  QUALITY_ANALYSIS_REJECT: 'quality_analysis.reject',
  
  // Génération de tickets
  TICKET_GENERATION_VIEW: 'ticket_generation.view',
  TICKET_GENERATION_CREATE: 'ticket_generation.create',
  TICKET_GENERATION_PRINT: 'ticket_generation.print',
  TICKET_GENERATION_EMAIL: 'ticket_generation.email',
  
  // Refoulement
  REJECTION_VIEW: 'rejection.view',
  REJECTION_CREATE: 'rejection.create',
  REJECTION_APPROVE: 'rejection.approve',
  
  // Gestion du stock
  STOCK_VIEW: 'stock.view',
  STOCK_CREATE: 'stock.create',
  STOCK_EDIT: 'stock.edit',
  STOCK_DELETE: 'stock.delete',
  STOCK_MANAGE_MOVEMENTS: 'stock.manage_movements',
  STOCK_MANAGE_WAREHOUSES: 'stock.manage_warehouses',
  
  // Gestion des magasins/entrepôts
  STORE_VIEW: 'store.view',
  STORE_CREATE: 'store.create',
  
  // Gestion des permissions
  PERMISSION_MANAGEMENT_VIEW: 'permission_management.view',
  PERMISSION_MANAGEMENT_CREATE: 'permission_management.create',
  PERMISSION_MANAGEMENT_EDIT: 'permission_management.edit',
  PERMISSION_MANAGEMENT_DELETE: 'permission_management.delete',
  PERMISSION_MANAGEMENT_ASSIGN: 'permission_management.assign',
  
  // Statistiques et rapports
  STATISTICS_VIEW: 'statistics.view',
  STATISTICS_EXPORT: 'statistics.export',
  STATISTICS_ADVANCED: 'statistics.advanced',
  
  // Export
  EXPORT_EXCEL: 'export.excel',
  EXPORT_PDF: 'export.pdf',
  EXPORT_CSV: 'export.csv',
  
  // Gestion multi-sites
  MULTI_SITE_VIEW: 'multi_site.view',
  MULTI_SITE_MANAGE: 'multi_site.manage',
  MULTI_SITE_SWITCH: 'multi_site.switch',
  
  // Gestion multi-tenant (nouvelle architecture)
  TENANT_MANAGEMENT_VIEW: 'tenant_management.view',
  TENANT_MANAGEMENT_CREATE: 'tenant_management.create',
  TENANT_MANAGEMENT_EDIT: 'tenant_management.edit',
  TENANT_MANAGEMENT_DELETE: 'tenant_management.delete',
  TENANT_MANAGEMENT_BLOCK: 'tenant_management.block',
  TENANT_MANAGEMENT_UNBLOCK: 'tenant_management.unblock',
  
  // Gestion des sites (nouvelle architecture)
  SITE_MANAGEMENT_VIEW: 'site_management.view',
  SITE_MANAGEMENT_CREATE: 'site_management.create',
  SITE_MANAGEMENT_EDIT: 'site_management.edit',
  SITE_MANAGEMENT_DELETE: 'site_management.delete',
  SITE_MANAGEMENT_SWITCH: 'site_management.switch',
  
  // Gestion des utilisateurs multi-tenant
  USER_TENANT_MANAGEMENT_VIEW: 'user_tenant_management.view',
  USER_TENANT_MANAGEMENT_CREATE: 'user_tenant_management.create',
  USER_TENANT_MANAGEMENT_EDIT: 'user_tenant_management.edit',
  USER_TENANT_MANAGEMENT_DELETE: 'user_tenant_management.delete',
  USER_TENANT_MANAGEMENT_ASSIGN_ROLES: 'user_tenant_management.assign_roles',
  
  // Invitations de tenants
  TENANT_INVITATION_VIEW: 'tenant_invitation.view',
  TENANT_INVITATION_CREATE: 'tenant_invitation.create',
  TENANT_INVITATION_ACCEPT: 'tenant_invitation.accept',
  TENANT_INVITATION_CANCEL: 'tenant_invitation.cancel',
  
  // Audit
  AUDIT_VIEW: 'audit.view',
  AUDIT_LOGS: 'audit.logs',
  AUDIT_REPORTS: 'audit.reports',
} as const;

// Types des codes de permissions
export type PermissionCode = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Définition des rôles de plateforme (nouvelle architecture)
export const PLATFORM_ROLES = {
  PLATFORM_SUPER_ADMIN: 'PLATFORM_SUPER_ADMIN',
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',
  PLATFORM_SUPPORT: 'PLATFORM_SUPPORT',
} as const;

// Définition des rôles opérationnels (nouvelle architecture)
export const OPERATIONAL_ROLES = {
  TENANT_OWNER: 'TENANT_OWNER',
  TENANT_ADMIN: 'TENANT_ADMIN',
  TENANT_MANAGER: 'TENANT_MANAGER',
  SITE_ADMIN: 'SITE_ADMIN',
  SITE_MANAGER: 'SITE_MANAGER',
  SITE_SUPERVISOR: 'SITE_SUPERVISOR',
  QUALITY_AGENT: 'QUALITY_AGENT',
  OPERATOR: 'OPERATOR',
  STORE_MANAGER: 'STORE_MANAGER',
  VIEWER: 'VIEWER',
} as const;

// Rôles par défaut (ancienne architecture - à déprécier)
export const DEFAULT_ROLES = {
  ADMIN_SYSTEM: 'admin_system',
  SUPERVISEUR: 'superviseur',
  OPERATEUR_PESEE: 'operateur_pesee',
  CONTROLEUR_QUALITE: 'controleur_qualite',
  STOCK: 'stock',
  COMMERCIAL: 'commercial',
  FINANCE: 'finance',
  TECHNICIEN: 'technicien',
  AUDITEUR: 'auditeur',
} as const;

export type PlatformRoleName = typeof PLATFORM_ROLES[keyof typeof PLATFORM_ROLES];
export type OperationalRoleName = typeof OPERATIONAL_ROLES[keyof typeof OPERATIONAL_ROLES];
export type RoleName = typeof DEFAULT_ROLES[keyof typeof DEFAULT_ROLES];

// Interface pour les permissions par rôle
export interface RolePermissions {
  [key: string]: PermissionCode[];
}

// Permissions par rôle (nouvelle architecture multi-tenant)
export const PLATFORM_ROLE_PERMISSIONS: RolePermissions = {
  [PLATFORM_ROLES.PLATFORM_SUPER_ADMIN]: [
    // Toutes les permissions
    ...Object.values(PERMISSIONS)
  ],
  [PLATFORM_ROLES.PLATFORM_ADMIN]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.TENANT_MANAGEMENT_CREATE,
    PERMISSIONS.TENANT_MANAGEMENT_EDIT,
    PERMISSIONS.TENANT_MANAGEMENT_BLOCK,
    PERMISSIONS.TENANT_MANAGEMENT_UNBLOCK,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_CREATE,
    PERMISSIONS.SITE_MANAGEMENT_EDIT,
    PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.USER_TENANT_MANAGEMENT_CREATE,
    PERMISSIONS.USER_TENANT_MANAGEMENT_EDIT,
    PERMISSIONS.USER_TENANT_MANAGEMENT_ASSIGN_ROLES,
    PERMISSIONS.TENANT_INVITATION_VIEW,
    PERMISSIONS.TENANT_INVITATION_CREATE,
    PERMISSIONS.TENANT_INVITATION_CANCEL,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_LOGS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
  ],
  [PLATFORM_ROLES.PLATFORM_SUPPORT]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.STATISTICS_VIEW,
  ],
};

export const OPERATIONAL_ROLE_PERMISSIONS: RolePermissions = {
  [OPERATIONAL_ROLES.TENANT_OWNER]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_CREATE,
    PERMISSIONS.SITE_MANAGEMENT_EDIT,
    PERMISSIONS.SITE_MANAGEMENT_DELETE,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.USER_TENANT_MANAGEMENT_CREATE,
    PERMISSIONS.USER_TENANT_MANAGEMENT_EDIT,
    PERMISSIONS.USER_TENANT_MANAGEMENT_DELETE,
    PERMISSIONS.USER_TENANT_MANAGEMENT_ASSIGN_ROLES,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_DELETE,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_DELETE,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STOCK_MANAGE_WAREHOUSES,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.STATISTICS_ADVANCED,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_LOGS,
  ],
  [OPERATIONAL_ROLES.TENANT_ADMIN]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.USER_TENANT_MANAGEMENT_CREATE,
    PERMISSIONS.USER_TENANT_MANAGEMENT_EDIT,
    PERMISSIONS.USER_TENANT_MANAGEMENT_ASSIGN_ROLES,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
    PERMISSIONS.AUDIT_VIEW,
  ],
  [OPERATIONAL_ROLES.TENANT_MANAGER]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [OPERATIONAL_ROLES.SITE_ADMIN]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.USER_TENANT_MANAGEMENT_VIEW,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [OPERATIONAL_ROLES.SITE_MANAGER]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [OPERATIONAL_ROLES.SITE_SUPERVISOR]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [OPERATIONAL_ROLES.QUALITY_AGENT]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  [OPERATIONAL_ROLES.OPERATOR]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  [OPERATIONAL_ROLES.STORE_MANAGER]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_DELETE,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STOCK_MANAGE_WAREHOUSES,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [OPERATIONAL_ROLES.VIEWER]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SITE_MANAGEMENT_VIEW,
    PERMISSIONS.SITE_MANAGEMENT_SWITCH,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_LOGS,
    PERMISSIONS.AUDIT_REPORTS,
  ],
};

// Permissions par rôle (ancienne architecture - à déprécier)
export const ROLE_PERMISSIONS: RolePermissions = {
  [DEFAULT_ROLES.ADMIN_SYSTEM]: [
    // Toutes les permissions
    ...Object.values(PERMISSIONS)
  ],
  [DEFAULT_ROLES.SUPERVISEUR]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL,
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.TICKET_GENERATION_EMAIL,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.STATISTICS_ADVANCED,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [DEFAULT_ROLES.OPERATEUR_PESEE]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.WEIGHING_CREATE,
    PERMISSIONS.WEIGHING_EDIT,
    PERMISSIONS.WEIGHING_CANCEL, // Limité
    PERMISSIONS.WEIGHING_COMPLETE,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  [DEFAULT_ROLES.CONTROLEUR_QUALITE]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_CREATE,
    PERMISSIONS.QUALITY_ANALYSIS_EDIT,
    PERMISSIONS.QUALITY_ANALYSIS_APPROVE,
    PERMISSIONS.QUALITY_ANALYSIS_REJECT,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.TICKET_GENERATION_CREATE,
    PERMISSIONS.TICKET_GENERATION_PRINT,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.REJECTION_CREATE,
    PERMISSIONS.REJECTION_APPROVE,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  [DEFAULT_ROLES.STOCK]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_CREATE,
    PERMISSIONS.STOCK_EDIT,
    PERMISSIONS.STOCK_DELETE,
    PERMISSIONS.STOCK_MANAGE_MOVEMENTS,
    PERMISSIONS.STOCK_MANAGE_WAREHOUSES,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [DEFAULT_ROLES.COMMERCIAL]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [DEFAULT_ROLES.FINANCE]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
  ],
  [DEFAULT_ROLES.TECHNICIEN]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.SYSTEM_CONFIG_VIEW,
    PERMISSIONS.SYSTEM_CONFIG_EDIT,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  [DEFAULT_ROLES.AUDITEUR]: [
    PERMISSIONS.ACCESS_APPLICATION,
    PERMISSIONS.WEIGHING_VIEW,
    PERMISSIONS.QUALITY_ANALYSIS_VIEW,
    PERMISSIONS.TICKET_GENERATION_VIEW,
    PERMISSIONS.REJECTION_VIEW,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.STATISTICS_EXPORT,
    PERMISSIONS.EXPORT_EXCEL,
    PERMISSIONS.EXPORT_PDF,
    PERMISSIONS.EXPORT_CSV,
    PERMISSIONS.MULTI_SITE_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_LOGS,
    PERMISSIONS.AUDIT_REPORTS,
  ],
};
